import { useNavigate } from 'react-router-dom';

const groups = [
  {
    number: '01',
    icon: 'ri-heart-pulse-line',
    title: 'Chronic Illness',
    subtitle: 'Fatigue, pain & unpredictable symptoms',
    description:
      'When your capacity shifts from day to day, the Boom & Bust cycle can feel impossible to escape. We start from the body you actually have right now, not the one you are told you should have.',
    points: ['Daily pain and symptom flares', 'Unpredictable fatigue and crashes', 'Brain fog and post-exertional payback'],
  },
  {
    number: '02',
    icon: 'ri-focus-3-line',
    title: 'ADHD & Autism',
    subtitle: 'Sensory overload, focus leakage & emotional drain',
    description:
      'Neurodivergent energy is not lazy or inconsistent — it is spent differently. We work with sensory load, hyperfocus, task-switching and masking instead of asking you to simply try harder.',
    points: ['Sensory overwhelm and shutdown', 'Hyperfocus followed by depletion', 'Masking, emotional and social drain'],
  },
  {
    number: '03',
    icon: 'ri-scales-3-line',
    title: 'Life-Demand Mismatch',
    subtitle: 'When daily responsibilities outpace your energy',
    description:
      'Some seasons ask more than a body can give. Work, caregiving, study and family load can exceed biological capacity — and no amount of willpower fixes a maths problem.',
    points: ['Responsibilities exceeding capacity', 'No permission to slow down', 'Constant low-level internal pressure'],
  },
];

export default function WhoIHelpSection() {
  const navigate = useNavigate();

  return (
    <section id="who-i-help" className="section-padding scroll-mt-24 bg-background-50">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <p className="font-body text-xs font-semibold uppercase tracking-widest text-accent-600 mb-3">
            Who I Support
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-800 mb-4">
            Three Kinds of Energy Drain, One Energy-Safe Approach
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
            In addition to supporting people through daily pain, unpredictable fatigue and brain fog, I also help
            individuals managing <strong className="text-primary-700">ADHD/Autism sensory drain</strong>, as well as
            those struggling when life&apos;s demands exceed their{' '}
            <strong className="text-primary-700">biological capacity</strong>.
          </p>
        </div>

        {/* Three Groups */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {groups.map((group) => (
            <div
              key={group.number}
              className="bg-background-100 rounded-xl p-7 flex flex-col hover:shadow-card hover:scale-[1.02] hover:-translate-y-[3px] transition-all duration-300 ease-out"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-200/40">
                  <i className={`${group.icon} text-lg text-accent-700`} />
                </div>
                <span className="font-body text-xs font-bold tracking-widest text-accent-700/60">
                  {group.number}
                </span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-primary-800 mb-1">
                {group.title}
              </h3>
              <p className="text-xs font-semibold uppercase tracking-wider text-accent-700 mb-3">
                {group.subtitle}
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                {group.description}
              </p>
              <ul className="space-y-2 mt-auto">
                {group.points.map((point) => (
                  <li key={point} className="flex items-start gap-2 text-sm text-foreground-600">
                    <span className="mt-1 w-4 h-4 flex items-center justify-center shrink-0">
                      <i className="ri-checkbox-circle-line text-accent-600" />
                    </span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Energy-Safe Motions */}
        <div className="max-w-4xl mx-auto rounded-xl border border-accent-300/40 bg-accent-50/40 p-7 md:p-9">
          <div className="flex flex-col md:flex-row md:items-start gap-5">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500 shrink-0">
              <i className="ri-route-line text-xl text-background-50" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-bold text-primary-800 mb-2">
                Energy-Safe Motions: the practical way out of Boom &amp; Bust
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                My <strong className="text-primary-700">Energy-Safe Method</strong> and the{' '}
                <strong className="text-primary-700">Energy-Safe Course</strong> are designed specifically for these
                three groups. Together, we use <strong className="text-primary-700">Energy-Safe Motions</strong> to
                break the Boom &amp; Bust cycle and build sustainable, guilt-free progress.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/course')}
                  className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-6 py-2.5 rounded-lg font-medium text-sm hover:bg-primary-600 hover:scale-[1.04] hover:-translate-y-[3px] transition-all duration-300 ease-out whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-book-open-line text-sm" />
                  </span>
                  Explore the Course
                </button>
                <a
                  href="https://calendly.com/federicatedeschi3/new-meeting"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border-2 border-primary-500 text-primary-700 px-6 py-2.5 rounded-lg font-medium text-sm hover:bg-primary-500 hover:text-background-50 hover:scale-[1.04] hover:-translate-y-[3px] transition-all duration-300 ease-out whitespace-nowrap cursor-pointer"
                >
                  Book a Free Conversation
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}